// LiveStreamGiftSlider.jsx

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import SwipeableViews from 'react-swipeable-views';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION, EMPTY_ARRAY } from '../resource/defaults.js';
import GiftCategory from '../container/GiftCategory.js';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';

import { color } from '../style/variables.js';
import GiftSendingIconSrc from '../../img/img-loading-dark-20.svg';
import GiftSendingSuccessIconSrc from '../../img/ic-complete-tealblue-36.svg';
import GiftSendingErrorIconSrc from '../../img/ic-close-red.svg';
import ErrorIconSrc from '../../img/chatroom-error.svg';

const GIFT_STATUS_DISPLAY_SECOND = 2000;

const tabStyle = {
  flex: 'none',
  marginRight: '16px',
  paddingRight: '0px',
  paddingLeft: '0px',
};
const labelStyle = {
  margin: '0px',
  paddingTop: '14px',
  paddingBottom: '8px',
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '1.5',
};

export class LiveStreamGiftSlider extends React.PureComponent {
  state = { selectedTabIndex: 0 };
  fetchRemoteConfig = () => {
    const { privateUserConfigId, fetchRemoteConfig } = this.props;
    if (!privateUserConfigId) {
      return;
    }
    return fetchRemoteConfig({ configId: privateUserConfigId });
  };
  componentDidMount() {
    this.fetchRemoteConfig();
  }
  componentDidUpdate(prevProp) {
    const { giftSendingStatus, privateUserConfigId, cleanGiftSendingStatus } =
      this.props;
    if (giftSendingStatus !== prevProp.giftSendingStatus) {
      if (giftSendingStatus === 'success' || giftSendingStatus === 'error') {
        this.statusTimeout = setTimeout(() => {
          cleanGiftSendingStatus();
        }, GIFT_STATUS_DISPLAY_SECOND);
      }
    }
    if (privateUserConfigId !== prevProp.privateUserConfigId) {
      this.fetchRemoteConfig();
    }
  }
  componentWillUnmount() {
    const { cleanGiftSendingStatus } = this.props;
    cleanGiftSendingStatus();
    clearTimeout(this.statusTimeout);
  }

  getCategoryI18nKey = id => `giftcategory_${id.replace(/-/g, '_')}`;

  handleTabClick = (event, index) => {
    this.setState({
      selectedTabIndex: index,
    });
  };

  handleSwipe = index => {
    this.setState({
      selectedTabIndex: index,
    });
  };

  handleSwitching = () => {
    this.props.setLiveStreamPanel({ isGiftPanelSwiping: true });
  };

  handleTransitionEnd = () => {
    this.props.setLiveStreamPanel({ isGiftPanelSwiping: false });
  };
  renderEmptyCategory = () => {
    const { t } = this.props;
    return (
      <EmptyCategory>
        <img src={ErrorIconSrc} alt="failed" />
        <EmptyCategoryText>
          {t('alert_load_failed', {
            ns: TranslationNamespace.GIFT,
          })}
        </EmptyCategoryText>
      </EmptyCategory>
    );
  };
  renderSlides() {
    const { streamId, categories, slideHeight, isCupidGiftEntryEnabled } =
      this.props;
    const { selectedTabIndex } = this.state;
    if (categories.length === 0) return this.renderEmptyCategory();
    return (
      <SwipeableViews
        index={selectedTabIndex}
        onChangeIndex={this.handleSwipe}
        onSwitching={this.handleSwitching}
        onTransitionEnd={this.handleTransitionEnd}
        enableMouseEvents
      >
        {categories.map((name, index) => (
          <GiftCategory
            key={name}
            category={name}
            userId={streamId}
            type={'livestream'}
            slideHeight={slideHeight}
            isActive={index === selectedTabIndex}
            shouldShowCupidGiftEntry={isCupidGiftEntryEnabled && index === 0}
          />
        ))}
      </SwipeableViews>
    );
  }

  renderTabs() {
    const { categories, t } = this.props;
    const { selectedTabIndex } = this.state;

    if (categories.length < 1) {
      return null;
    }

    return (
      <TabsWrapper>
        <Tabs
          value={selectedTabIndex}
          onChange={this.handleTabClick}
          isScrollable={true}
        >
          {categories.map((category, i) => {
            const label = t(
              this.getCategoryI18nKey(category, {
                ns: TranslationNamespace.GIFT,
              })
            );

            return (
              <Tab
                key={category}
                value={i}
                label={label}
                style={tabStyle}
                labelStyle={labelStyle}
              />
            );
          })}
        </Tabs>
      </TabsWrapper>
    );
  }

  renderGiftSendingStatus = () => {
    const { t, giftSendingStatus } = this.props;

    if (!giftSendingStatus) return null;
    let statusImageElement,
      statusImageSrc,
      statusTextKey = undefined;
    if (giftSendingStatus === 'sending') {
      statusTextKey = 'toast_sending_gift';
      statusImageElement = (
        <img src={GiftSendingIconSrc} width="32" height="32" />
      );
    } else {
      statusTextKey =
        giftSendingStatus === 'success'
          ? 'toast_gift_sent'
          : 'toast_send_gift_failed';
      statusImageSrc =
        giftSendingStatus === 'success'
          ? GiftSendingSuccessIconSrc
          : GiftSendingErrorIconSrc;
      statusImageElement = (
        <GiftSendingStatusImageWrapper type={giftSendingStatus}>
          <img src={statusImageSrc} width="16" height="16" />
        </GiftSendingStatusImageWrapper>
      );
    }

    return (
      <GiftSendingStatusWrapper>
        {statusImageElement}
        <GiftSendingStatusText>
          {t(statusTextKey, {
            ns: TranslationNamespace.GIFT,
          })}
        </GiftSendingStatusText>
      </GiftSendingStatusWrapper>
    );
  };

  render() {
    return (
      <StyledLiveStreamGiftSlider>
        {this.renderTabs()}
        {this.renderSlides()}
        {this.renderGiftSendingStatus()}
      </StyledLiveStreamGiftSlider>
    );
  }
}

LiveStreamGiftSlider.propTypes = {
  t: PropTypes.func.isRequired,
  streamId: PropTypes.string,
  privateUserConfigId: PropTypes.string,
  categories: PropTypes.array,
  setLiveStreamPanel: PropTypes.func,
  giftSendingStatus: PropTypes.string,
  cleanGiftSendingStatus: PropTypes.func,
  fetchRemoteConfig: PropTypes.func,
  slideHeight: PropTypes.number,
  isCupidGiftEntryEnabled: PropTypes.bool,
};

LiveStreamGiftSlider.defaultProps = {
  streamId: '',
  status: '',
  privateUserConfigId: null,
  categories: EMPTY_ARRAY,
  setLiveStreamPanel: NULL_FUNCTION,
  giftSendingStatus: '',
  cleanGiftSendingStatus: NULL_FUNCTION,
  fetchRemoteConfig: NULL_FUNCTION,
  slideHeight: 300,
  isCupidGiftEntryEnabled: false,
};

const StyledLiveStreamGiftSlider = styled.div`
  position: relative;
  width: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TabsWrapper = styled.div`
  box-sizing: content-box;
  background: ${color.neutral[10]};
  border-bottom: 1px solid ${color.neutral['20_alpha_40']};
`;

const GiftSendingStatusWrapper = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  width: 100%;
  background-color: ${color.neutral['0_alpha_80']};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const GiftSendingStatusImageWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border-style: solid;
  border-width: 2px;
  border-color: ${({ type }) =>
    type === 'success' ? color.tealBlue : color.red};
`;

const GiftSendingStatusText = styled.div`
  margin-top: 16px;
  font-size: 12px;
  font-weight: 500;
  color: ${color.white};
`;
const EmptyCategory = styled.div`
  height: 290px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
const EmptyCategoryText = styled.div`
  margin-top: 12px;
  font-size: 14px;
  color: ${color.neutral[30]};
`;

export default withTranslation()(LiveStreamGiftSlider);
